import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, AgentNumber, CallLog, Campaign, RuntimeSnap } from "../lib/api";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"] as const;

export function DialerPage({ csrf }: { csrf: string }) {
  const [phone, setPhone] = useState("");
  const [script, setScript] = useState("");
  const [assignedNumbers, setAssignedNumbers] = useState<AgentNumber[]>([]);
  const [selectedNumberId, setSelectedNumberId] = useState("");
  const [dialBusy, setDialBusy] = useState(false);
  const [hangupBusy, setHangupBusy] = useState(false);
  const [currentCall, setCurrentCall] = useState<CallLog | null>(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [recent, setRecent] = useState<CallLog[]>([]);

  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [runtimes, setRuntimes] = useState<Record<string, RuntimeSnap>>({});
  const [selectedId, setSelectedId] = useState("");
  const [loaded, setLoaded] = useState<Campaign | null>(null);
  const [runtime, setRuntime] = useState<RuntimeSnap | null>(null);
  const [campaignBusy, setCampaignBusy] = useState(false);

  const refreshCampaigns = useCallback(async () => {
    const data = await api.listCampaigns();
    setCampaigns(data.campaigns);
    setRuntimes(data.runtimes || {});
  }, []);

  const refreshRecent = useCallback(async () => {
    const logs = await api.listCalls();
    setRecent(logs.calls.slice(0, 10));
  }, []);

  useEffect(() => {
    Promise.all([refreshCampaigns(), refreshRecent()]).catch((e: unknown) =>
      setError(e instanceof Error ? e.message : "Failed to load dialer")
    );
    api.getCampaignRouting().then((r) => {
      const rows = (r.numbers || []).filter((n) => n.outboundEnabled !== false);
      setAssignedNumbers(rows);
      setSelectedNumberId((current) => current || r.staff?.defaultOutboundNumberId || rows[0]?.id || "");
    }).catch(() => {});
  }, [refreshCampaigns, refreshRecent]);

  // Live poll while a campaign is running or after load
  useEffect(() => {
    if (!loaded?.id) return;
    const t = setInterval(() => {
      api
        .getCampaign(loaded.id)
        .then((data) => {
          setLoaded(data.campaign);
          setRuntime(data.runtime);
        })
        .catch(() => {});
      refreshCampaigns().catch(() => {});
    }, 3000);
    return () => clearInterval(t);
  }, [loaded?.id, refreshCampaigns]);

  function appendKey(k: string) {
    setPhone((p) => (p + k).slice(0, 24));
    setSuccess("");
    setError("");
  }

  function backspace() {
    setPhone((p) => p.slice(0, -1));
  }

  function clearPhone() {
    setPhone("");
  }

  async function dialSingle() {
    setDialBusy(true);
    setError("");
    setSuccess("");
    try {
      const res = await api.singleDial(
        {
          phone,
          messageScript: script.trim() || undefined,
          numberId: selectedNumberId || undefined
        },
        csrf
      );
      setCurrentCall(res.call);
      setSuccess(`Dialing ${res.call.phone} · status ${res.call.status}`);
      await refreshRecent();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Dial failed");
    } finally {
      setDialBusy(false);
    }
  }

  async function endCall() {
    if (!currentCall?.id) return;

    setHangupBusy(true);
    setError("");
    setSuccess("");

    try {
      await api.hangupCall(currentCall.id, csrf);
      setCurrentCall(null);
      setSuccess("Call ended");
      await refreshRecent();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Could not end call");
    } finally {
      setHangupBusy(false);
    }
  }

  async function loadCampaign() {
    if (!selectedId) {
      setError("Select a campaign to load");
      return;
    }
    setCampaignBusy(true);
    setError("");
    setSuccess("");
    try {
      const data = await api.getCampaign(selectedId);
      setLoaded(data.campaign);
      setRuntime(data.runtime);
      setSuccess(`Loaded campaign “${data.campaign.name}” (${data.campaign.status})`);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Could not load campaign");
    } finally {
      setCampaignBusy(false);
    }
  }

  async function campaignAct(action: "start" | "pause" | "stop") {
    if (!loaded?.id) {
      setError("Load a campaign first");
      return;
    }
    setCampaignBusy(true);
    setError("");
    setSuccess("");
    try {
      const res = await api.campaignAction(loaded.id, action, csrf);
      setLoaded(res.campaign);
      setRuntime(res.runtime);
      setSuccess(
        action === "start"
          ? `Campaign dialing started · queue ${res.runtime?.queued ?? 0}`
          : action === "pause"
            ? "Campaign paused"
            : "Campaign stopped"
      );
      await refreshCampaigns();
      await refreshRecent();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Campaign action failed");
    } finally {
      setCampaignBusy(false);
    }
  }

  const liveChannels = useMemo(() => {
    if (runtime) return `${runtime.activeCalls}/${runtime.maxConcurrent}`;
    if (loaded && runtimes[loaded.id]) {
      const r = runtimes[loaded.id];
      return `${r.activeCalls}/${r.maxConcurrent}`;
    }
    return null;
  }, [runtime, loaded, runtimes]);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Dialer</h1>
          <p>
            Use the dialpad for a single outbound call, or load a campaign and start multi-channel
            autodialing.
          </p>
        </div>
        <div className="row-actions">
          <Link className="btn ghost" to="/settings">
            Provider settings
          </Link>
          <Link className="btn ghost" to="/campaigns">
            Manage campaigns
          </Link>
        </div>
      </div>

      {error ? <div className="alert error">{error}</div> : null}
      {success ? <div className="alert success">{success}</div> : null}

      <div className="dialer-layout">
        {/* Single dialpad */}
        <section className="card dialer-panel">
          <h2>Single dial · Dialpad</h2>
          <p className="muted" style={{ marginTop: 0 }}>
            Places one outbound call via your Twilio / Telnyx / SIP settings. On answer, delivers your
            TTS / AI voice script.
          </p>

          <input
            className="dial-display"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="+1 876 555 1234"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (phone.trim()) void dialSingle();
              }
            }}
            aria-label="Phone number"
          />

          <div className="keypad" role="group" aria-label="Dialpad">
            {KEYS.map((k) => (
              <button key={k} type="button" className="key" onClick={() => appendKey(k)}>
                {k}
              </button>
            ))}
          </div>

          <div className="row-actions" style={{ marginBottom: "0.85rem" }}>
            <button className="btn ghost" type="button" onClick={backspace} aria-label="Backspace">
              ⌫ Delete
            </button>
            <button className="btn ghost" type="button" onClick={clearPhone}>
              Clear
            </button>
            <button
              className="btn success"
              type="button"
              disabled={dialBusy || hangupBusy || Boolean(currentCall) || !phone.trim()}
              onClick={() => void dialSingle()}
            >
              {dialBusy ? "Dialing…" : "Call"}
            </button>

            <button
              className="btn danger"
              type="button"
              disabled={!currentCall || hangupBusy}
              onClick={() => void endCall()}
            >
              {hangupBusy ? "Ending…" : "End Call"}
            </button>
          </div>

          <label>
            <strong>Call from</strong>
            <select value={selectedNumberId} onChange={(e) => setSelectedNumberId(e.target.value)} aria-label="Call from assigned number">
              <option value="">Select an assigned number</option>
              {assignedNumbers.map((n) => (
                <option key={n.id} value={n.id}>{n.displayName || n.label || n.phone || n.e164}</option>
              ))}
            </select>
          </label>
          <label>
            <strong>Message after answer (optional)</strong>
            <textarea
              value={script}
              onChange={(e) => setScript(e.target.value)}
              placeholder="Leave blank to use your default TTS script from Settings"
            />
          </label>
        </section>

        {/* Campaign dialer */}
        <section className="card dialer-panel">
          <h2>Campaign dialer</h2>
          <p className="muted" style={{ marginTop: 0 }}>
            Load a saved campaign, then start dialing. The engine fills up to the campaign’s max
            concurrent channels and speaks your script when contacts answer.
          </p>

          <label>
            <strong>Select campaign</strong>
            <select
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              aria-label="Campaign to load"
            >
              <option value="">— Choose campaign —</option>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} · {c.status} · {c.maxConcurrent} ch
                </option>
              ))}
            </select>
          </label>

          <div className="row-actions" style={{ marginBottom: "1rem" }}>
            <button
              className="btn"
              type="button"
              disabled={campaignBusy || !selectedId}
              onClick={() => void loadCampaign()}
            >
              Load campaign
            </button>
            <Link className="btn ghost" to="/campaigns">
              Create new
            </Link>
          </div>

          {loaded ? (
            <div className="campaign-loaded">
              <div className="campaign-loaded-head">
                <div>
                  <strong>{loaded.name}</strong>
                  <div className="muted" style={{ marginTop: 4 }}>
                    Status <span className={`badge ${loaded.status}`}>{loaded.status}</span>
                    {liveChannels ? <> · live channels {liveChannels}</> : null}
                    {runtime ? <> · queue {runtime.queued}</> : null}
                  </div>
                </div>
                <Link className="btn ghost sm" to={`/campaigns/${loaded.id}`}>
                  Full details
                </Link>
              </div>

              <div className="grid-stats" style={{ marginTop: "0.85rem", marginBottom: "0.85rem" }}>
                <div className="stat-card">
                  <div className="label">Pending</div>
                  <div className="value">{loaded.stats?.pending ?? 0}</div>
                </div>
                <div className="stat-card">
                  <div className="label">Answered</div>
                  <div className="value">{loaded.stats?.answered ?? 0}</div>
                </div>
                <div className="stat-card">
                  <div className="label">Completed</div>
                  <div className="value">{loaded.stats?.completed ?? 0}</div>
                </div>
                <div className="stat-card">
                  <div className="label">Failed / NA</div>
                  <div className="value">
                    {(loaded.stats?.failed ?? 0) + (loaded.stats?.noAnswer ?? 0)}
                  </div>
                </div>
              </div>

              <p className="muted" style={{ fontSize: "0.88rem", whiteSpace: "pre-wrap" }}>
                {loaded.messageScript?.slice(0, 280)}
                {(loaded.messageScript?.length || 0) > 280 ? "…" : ""}
              </p>

              <div className="row-actions">
                <button
                  className="btn success"
                  type="button"
                  disabled={campaignBusy || loaded.status === "running"}
                  onClick={() => void campaignAct("start")}
                >
                  {loaded.status === "running" ? "Dialing…" : "Start dialing"}
                </button>
                <button
                  className="btn ghost"
                  type="button"
                  disabled={campaignBusy || loaded.status !== "running"}
                  onClick={() => void campaignAct("pause")}
                >
                  Pause
                </button>
                <button
                  className="btn danger"
                  type="button"
                  disabled={campaignBusy || !["running", "paused"].includes(loaded.status)}
                  onClick={() => void campaignAct("stop")}
                >
                  Stop
                </button>
              </div>
            </div>
          ) : (
            <div className="empty-dial-hint">
              <p className="muted">
                No campaign loaded. Pick one above and click <strong>Load campaign</strong>, then{" "}
                <strong>Start dialing</strong> to initiate outbound calls.
              </p>
            </div>
          )}
        </section>
      </div>

      <div className="card" style={{ marginTop: "1rem" }}>
        <div className="section-title">
          <h2>Recent calls</h2>
          <button className="btn ghost sm" type="button" onClick={() => void refreshRecent()}>
            Refresh
          </button>
        </div>
        {recent.length === 0 ? (
          <p className="muted">No calls yet. Use the dialpad or start a campaign.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Phone</th>
                <th>Status</th>
                <th>Mode</th>
                <th>Provider</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((c) => (
                <tr key={c.id}>
                  <td className="mono">{c.phone}</td>
                  <td>
                    <span className={`badge ${c.status}`}>{c.status}</span>
                  </td>
                  <td className="muted">{c.campaignId ? "campaign" : "single"}</td>
                  <td>{c.provider}</td>
                  <td className="muted">{new Date(c.createdAt).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
